// @ts-nocheck
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  createRef
} from 'react';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { XGDropdownMenu } from 'xg-cl';
import LineItemFieldRenderer from './LineItemFieldRenderer';
import {
  getLineItemFields,
  getRequiredFields
} from '../../../constants/lineItemFieldConfig';
import {
  getLineItemServiceType,
  getAllLineItemServiceTypes
} from '../../../constants/lineItemServiceTypes';
import * as PropTypes from 'prop-types';
import './LineItemForm.css';

/**
 * LineItemForm component for creating and editing line items.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.lineItem - The line item being edited (null for new items).
 * @param {function} props.onSave - Callback function when saving.
 * @param {function} props.onCancel - Callback function when canceling.
 * @param {function} props.onDelete - Callback function when deleting.
 * @param {boolean} props.canDelete - Whether delete is allowed.
 * @param {boolean} props.isLibrary - Whether this is a library item.
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting.
 * @param {number} props.tripId - Trip ID for file uploads.
 * @param {number} props.tenantId - Tenant ID for file uploads.
 *
 * @returns {JSX.Element} A JSX element representing the line item form.
 */
const LineItemForm = ({
  lineItem = null,
  onSave,
  onCancel,
  onDelete,
  canDelete = false,
  isLibrary = false,
  isSubmitting = false,
  tripId,
  tenantId,
  hideActions = false,
  setSubmitHandler = null,
  onUnsavedChange = null,
  formData,
  setFormData,
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common']);
  const [errors, setErrors] = useState({});
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const initialFormDataRef = useRef(null);

  const fieldRefs = useMemo(() => {
    const refs = {};
    const fields = getLineItemFields(formData.servicetypecode);
    fields.forEach((field) => {
      refs[field.field] = createRef();
    });
    return refs;
  }, [formData.servicetypecode]);

  // Focus the "title" field when the component mounts
  useEffect(() => {
    if (fieldRefs.title?.current) {
      fieldRefs.title.current.focus();
    }
  }, [fieldRefs]);

  const serviceContainsField = (serviceTypeCode, fieldName) => {
    const fields = getLineItemFields(serviceTypeCode);

    return fields.some((field) => field?.field === fieldName);
  };

  const getDefaultExpandedAccordions = (serviceTypeCode, formData = {}) => {
    const defs = getLineItemFields(serviceTypeCode);
    const expanded = {};
    defs.forEach((def) => {
      if (def?.accordion) {
        // Expand accordion only if the field has a non-empty value
        const fieldValue = formData[def.field];
        const hasValue =
          fieldValue !== null &&
          fieldValue !== undefined &&
          fieldValue !== '' &&
          (typeof fieldValue === 'string' ? fieldValue.trim() !== '' : true);
        expanded[def.field] = hasValue;
      }
    });
    return expanded;
  };

  useEffect(() => {
    if (lineItem && lineItem.lineitemid) {
      // Initialize form data from existing line item (editing mode)
      const normalizedEndDayOffset =
        lineItem.enddayoffset === null || lineItem.enddayoffset === undefined
          ? 0
          : Number(lineItem.enddayoffset);

      const initialData = {
        servicetypecode: lineItem.servicetypecode || '',
        title: lineItem.title || '',
        description: lineItem.description || '',
        vendorname: lineItem.vendorname || '',
        vendorphone: lineItem.vendorphone || '',
        vendorlink: lineItem.vendorlink || '',
        contactname: lineItem.contactname || '',
        startlocation: lineItem.startlocation || '',
        endlocation: lineItem.endlocation || '',
        starttime: lineItem.starttime || '',
        endtime: lineItem.endtime || '',
        durationtime: lineItem.durationtime || '',
        confirmationnumber: lineItem.confirmationnumber || '',
        serviceclass: lineItem.serviceclass || '',
        servicenumber: lineItem.servicenumber || '',
        enddayoffset: normalizedEndDayOffset,
        arrivesnextday: serviceContainsField(
          lineItem?.servicetypecode || '',
          'arrivesnextday'
        )
          ? normalizedEndDayOffset === 1
          : undefined,
        mealsincluded: lineItem.mealsincluded
          ? lineItem.mealsincluded.split(',').map((m) => m.trim())
          : [],
        internalnote: lineItem.internalnote || '',
        images: lineItem.lineitemimage || [],
        attachments: lineItem.lineitemattachment || []
      };
      setFormData(initialData);
      initialFormDataRef.current = initialData;
      setExpandedAccordions(
        getDefaultExpandedAccordions(initialData.servicetypecode, initialData)
      );
    } else if (lineItem && lineItem.servicetypecode) {
      // Initialize new form with pre-selected service type from dropdown
      const initialData = {
        servicetypecode: lineItem.servicetypecode,
        title: '',
        description: '',
        enddayoffset: 0,
        arrivesnextday: lineItem.servicetypecode === 'FLT' ? false : undefined
      };
      setFormData(initialData);
      initialFormDataRef.current = initialData;
      setExpandedAccordions(
        getDefaultExpandedAccordions(initialData.servicetypecode, initialData)
      );
    } else {
      // Initialize completely empty form
      const initialData = {
        servicetypecode: '',
        title: '',
        description: '',
        enddayoffset: 0,
        arrivesnextday: undefined
      };
      setFormData(initialData);
      initialFormDataRef.current = initialData;
      setExpandedAccordions({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItem]);

  useEffect(() => {
    if (typeof onUnsavedChange !== 'function') return;
    if (!initialFormDataRef.current) return;

    const stableStringify = (obj) => {
      try {
        return JSON.stringify(obj);
      } catch (e) {
        return '';
      }
    };

    const hasChanges =
      stableStringify(formData) !== stableStringify(initialFormDataRef.current);

    try {
      onUnsavedChange(hasChanges);
    } catch (e) {
      /* no-op */
    }
  }, [formData, onUnsavedChange]);

  const serviceTypeOptions = getAllLineItemServiceTypes();
  const fields = getLineItemFields(formData.servicetypecode);

  const generateGridColumns = (rowFields = []) => {
    const sortedFields = [...rowFields].sort(
      (a, b) => (a?.col ?? 0) - (b?.col ?? 0)
    );
    const columnsList = [];
    let currentColumn = 1;

    for (const field of sortedFields) {
      const col = field?.col ?? 1;
      const colsize = field?.colsize ?? 12;
      const diff = col - currentColumn;
      if (diff > 0) {
        columnsList.push({
          colsize: diff,
          col: currentColumn,
          isEmptyColumn: true
        });
      }
      columnsList.push({ ...field, col, colsize, isEmptyColumn: false });
      currentColumn = col + colsize;
    }

    if (currentColumn <= 12) {
      columnsList.push({
        colsize: 12 - currentColumn + 1,
        col: currentColumn,
        isEmptyColumn: true
      });
    }

    return columnsList;
  };

  const generateGridRows = (rowFields = []) => {
    return rowFields.reduce((acc, field) => {
      const row = field?.row ?? 1;
      if (!acc[row]) acc[row] = [];
      acc[row].push(field);
      return acc;
    }, {});
  };

  const renderFields = () => {
    if (!Array.isArray(fields) || fields.length === 0) return null;

    const hasGridLayout = fields.some((f) => typeof f?.row === 'number');

    if (!hasGridLayout) {
      return fields.map((fieldDef) => (
        <LineItemFieldRenderer
          key={fieldDef.field}
          fieldDef={fieldDef}
          value={formData[fieldDef.field]}
          onChange={handleFieldChange}
          onError={handleFieldError}
          error={errors[fieldDef.field]}
          disabled={isSubmitting || (fieldDef?.tripOnly && isLibrary)}
          expanded={expandedAccordions[fieldDef.field]}
          onAccordionChange={() => handleAccordionChange(fieldDef.field)}
          tripId={tripId}
          tenantId={tenantId}
          isLibrary={isLibrary}
          inputRef={fieldRefs[fieldDef.field]}
        />
      ));
    }

    const rows = generateGridRows(fields);
    const rowNumbers = Object.keys(rows)
      .map((n) => Number(n))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);

    return (
      <Grid container spacing={2} sx={{ gap: '24px' }}>
        {rowNumbers.map((rowNumber) => {
          const gridRowColumns = generateGridColumns(rows[rowNumber] || []);
          return (
            <Grid item xs={12} key={rowNumber}>
              <Grid container spacing={2} sx={{ alignItems: 'end' }}>
                {gridRowColumns.map((fieldDef, index) => {
                  if (fieldDef?.isEmptyColumn) {
                    return (
                      <Grid
                        item
                        xs={fieldDef.colsize}
                        key={`empty-${rowNumber}-${index}`}
                      />
                    );
                  }

                  return (
                    <Grid
                      item
                      xs={fieldDef?.colsize ?? 12}
                      key={fieldDef.field}
                      style={{ paddingTop: '0' }}
                    >
                      <LineItemFieldRenderer
                        fieldDef={fieldDef}
                        value={formData[fieldDef.field]}
                        onChange={handleFieldChange}
                        onError={handleFieldError}
                        error={errors[fieldDef.field]}
                        disabled={
                          isSubmitting || (fieldDef?.tripOnly && isLibrary)
                        }
                        expanded={expandedAccordions[fieldDef.field]}
                        onAccordionChange={() =>
                          handleAccordionChange(fieldDef.field)
                        }
                        tripId={tripId}
                        tenantId={tenantId}
                        isLibrary={isLibrary}
                        inputRef={fieldRefs[fieldDef.field]}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const handleServiceTypeChange = (code) => {
    const serviceType = getLineItemServiceType(code);
    const newFormData = {
      servicetypecode: code,
      title: serviceType?.name || '',
      description: '',
      enddayoffset: 0,
      arrivesnextday: code === 'FLT' ? false : undefined
    };
    setFormData(newFormData);
    setErrors({});
    setExpandedAccordions(getDefaultExpandedAccordions(code, newFormData));
  };

  const handleFieldChange = (fieldName, value) => {
    if (fieldName === 'arrivesnextday') {
      const nextChecked = Boolean(value);
      setFormData({
        ...formData,
        arrivesnextday: nextChecked,
        enddayoffset: nextChecked ? 1 : 0
      });
    } else {
      setFormData({
        ...formData,
        [fieldName]: value
      });
    }
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: null });
    }
  };

  const handleFieldError = (fieldName, errorMessage) => {
    setErrors({
      ...errors,
      [fieldName]: errorMessage
    });
  };

  const handleAccordionChange = (fieldName) => {
    setExpandedAccordions({
      ...expandedAccordions,
      [fieldName]: !expandedAccordions[fieldName]
    });
  };

  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.servicetypecode) {
      newErrors.servicetypecode = t('common:required');
    }

    // Validate required fields based on service type
    const requiredFields = getRequiredFields(formData.servicetypecode);
    requiredFields.forEach((field) => {
      if (
        !formData[field?.field] ||
        (typeof formData[field?.field] === 'string' &&
          !formData[field?.field].trim())
      ) {
        newErrors[field?.field] = `${field?.label}  is ${t('common:required')}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(() => {
    if (!validate()) {
      console.log('❌ LineItem Validation failed');
      return;
    }

    if (onSave) {
      // Auto-prefix vendorlink with https:// if it doesn't have a protocol
      const normalizedVendorLink = formData.vendorlink
        ? (() => {
            const trimmedLink = formData.vendorlink.trim();
            if (!trimmedLink) return null;
            // Check if it already has a protocol (http://, https://, ftp://, etc.)
            if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmedLink)) {
              return trimmedLink;
            }
            // Auto-prefix with https://
            return `https://${trimmedLink}`;
          })()
        : null;

      // Prepare payload - trim whitespace from all string fields
      const payload = {
        servicetypecode: trimFieldValue(formData.servicetypecode),
        title: trimFieldValue(formData.title),
        description: trimFieldValue(formData.description) || null,
        vendorname: trimFieldValue(formData.vendorname) || null,
        vendorphone: trimFieldValue(formData.vendorphone) || null,
        vendorlink: normalizedVendorLink,
        contactname: trimFieldValue(formData.contactname) || null,
        startlocation: trimFieldValue(formData.startlocation) || null,
        endlocation: trimFieldValue(formData.endlocation) || null,
        starttime: trimFieldValue(formData.starttime) || null,
        endtime: trimFieldValue(formData.endtime) || null,
        durationtime: trimFieldValue(formData.durationtime) || null,
        confirmationnumber: trimFieldValue(formData.confirmationnumber) || null,
        serviceclass: trimFieldValue(formData.serviceclass) || null,
        servicenumber: trimFieldValue(formData.servicenumber) || null,
        enddayoffset:
          formData.enddayoffset === null || formData.enddayoffset === undefined
            ? null
            : parseInt(formData.enddayoffset, 10),
        mealsincluded:
          Array.isArray(formData.mealsincluded) &&
          formData.mealsincluded.length > 0
            ? formData.mealsincluded
                .map((meal) => trimFieldValue(meal))
                .filter(Boolean)
                .join(', ')
            : null,
        internalnote: trimFieldValue(formData.internalnote) || null,
        images: formData.images || [],
        attachments: formData.attachments || []
      };
      onSave(payload);
    }
  }, [formData, validate, onSave]);

  const submitRef = useRef(handleSubmit);

  useEffect(() => {
    submitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    if (!setSubmitHandler) return;
    setSubmitHandler(() => () => submitRef.current());
  }, [setSubmitHandler]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  // Helper function to trim whitespace from form field values
  const trimFieldValue = (value) => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    }
    return value;
  };

  return (
    <Box className='line-item-form' {...rest}>
      {/* Service Type Selector - Only show for new items */}
      {!lineItem && (
        <Box sx={{ mb: 2 }}>
          <XGDropdownMenu
            id='line-item-service-type'
            label={t('trip:serviceType')}
            value={formData.servicetypecode}
            onChange={handleServiceTypeChange}
            options={serviceTypeOptions.map((st) => ({
              label: st.name,
              value: st.code
            }))}
            error={!!errors.servicetypecode}
            helperText={errors.servicetypecode}
            disabled={isSubmitting}
            required
          />
        </Box>
      )}

      {/* Dynamic Fields based on Service Type */}
      {formData.servicetypecode && renderFields()}

      {/* Action Buttons */}
      {!hideActions && (
        <Box
          display='flex'
          gap={2}
          justifyContent='space-between'
          sx={{ mt: 3 }}
        >
          <Box>
            {canDelete && lineItem && (
              <Button
                variant='outlined'
                color='error'
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                {t('common:delete')}
              </Button>
            )}
          </Box>
          <Box display='flex' gap={2}>
            <Button
              variant='outlined'
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : lineItem ? (
                t('common:saveChanges')
              ) : (
                t('common:add')
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

LineItemForm.propTypes = {
  lineItem: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  //onSaveToLibrary: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  canDelete: PropTypes.bool,
  isLibrary: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  tripId: PropTypes.number,
  tenantId: PropTypes.number
};

export default LineItemForm;
